/**
 * Product Builder Vue 3 Application
 * Manages custom product configuration with print options
 */

if (!customElements.get('product-builder-form')) {
  class ProductBuilderForm extends HTMLElement {
    constructor() {
      super();
      this.app = null;
      this.mounted = false;
    }

    connectedCallback() {
      if (this.mounted) return;
      this.mounted = true;

      // Wait for Vue to be available
      this.initializeApp();
    }

    async initializeApp() {
      // Wait for Vue 3 to be loaded
      if (typeof Vue === 'undefined') {
        console.error('Vue 3 is not loaded. Please include Vue 3 in your theme.');
        return;
      }

      const config = JSON.parse(this.querySelector('[data-builder-config]')?.textContent || '{}');

      const { createApp } = Vue;

      this.app = createApp({
        delimiters: ['${', '}'],
        data() {
          return {
            submitted: false,
            success: false,
            showBrief: false,
            selectedColourName: config.defaultColour || null,
            showColourOnPreview: false,
            cartLimit: null,
            selection: 1,
            selectedPrints: [{
              selection: 0,
              id: null,
              type: null,
            }],
            defaultSizeId: config.defaultSizeId || null,
            sizeIndex: config.sizeIndex || null,
            options: [],
            buyType: null,
            printBrief: null,
            variants: [],
            positions: Array.isArray(config.positions) ? config.positions : [],
            screenPrintContextMenu: null, // Track which print has screen print menu open
            showServices: false, // Control visibility of additional services section
            selectedServices: [], // Array of selected service IDs
            additionalServices: config.additionalServices || [], // Available additional services
            heroOptions: [
              {
                name: 'Screen Print',
                colours: '1 - 12',
                minimum: '20x',
                cost: config.screenCost || 0,
                image: config.screenIcon || '',
                variant: 'SP 1 CLR - First Print',
              },
              {
                name: 'Digital Print',
                colours: 'Unlimited',
                minimum: '1x',
                cost: config.dtfCost || 0,
                image: config.dtfIcon || '',
                variant: 'First Print',
              },
              {
                name: 'Embroidery',
                colours: 'Up to 12',
                minimum: '10x',
                cost: config.embroideryCost || 0,
                image: config.embroideryIcon || '',
                variant: 'First Embroidery',
              },
            ],
            availablePrintTypes: [
              {
                name: 'Screen Print',
                icon: config.screen1Icon || '',
                isParent: true,
                moq: 20,
              },
              {
                name: 'Digital Print',
                icon: config.dtfPrintIcon || '',
                variant_first: 'First Print',
                variant_additional: 'Additional Print',
                moq: 0
              },
              {
                name: 'Embroidery',
                icon: config.embroideryPrintIcon || '',
                variant_first: 'First Embroidery',
                variant_additional: 'Additional Embroidery',
                moq: 10
              }
            ],
            screenPrintOptions: [
              { name: '1 Colour Screenprint', variant_first: 'SP 1 CLR - First Print', variant_additional: 'SP 1 CLR - Additional Print', moq: 20, colours: 1 },
              { name: '2 Colour Screenprint', variant_first: 'SP 2 CLR - First Print', variant_additional: 'SP 2 CLR - Additional Print', moq: 20, colours: 2 },
              { name: '3 Colour Screenprint', variant_first: 'SP 3 CLR - First Print', variant_additional: 'SP 3 CLR - Additional Print', moq: 20, colours: 3 },
              { name: '4 Colour Screenprint', variant_first: 'SP 4 CLR - First Print', variant_additional: 'SP 4 CLR - Additional Print', moq: 20, colours: 4 },
              { name: '5 Colour Screenprint', variant_first: 'SP 5 CLR - First Print', variant_additional: 'SP 5 CLR - Additional Print', moq: 20, colours: 5 },
              { name: '6 Colour Screenprint', variant_first: 'SP 6 CLR - First Print', variant_additional: 'SP 6 CLR - Additional Print', moq: 20, colours: 6 },
              { name: '7 Colour Screenprint', variant_first: 'SP 7 CLR - First Print', variant_additional: 'SP 7 CLR - Additional Print', moq: 20, colours: 7 },
              { name: '8 Colour Screenprint', variant_first: 'SP 8 CLR - First Print', variant_additional: 'SP 8 CLR - Additional Print', moq: 20, colours: 8 },
              { name: '9 Colour Screenprint', variant_first: 'SP 9 CLR - First Print', variant_additional: 'SP 9 CLR - Additional Print', moq: 20, colours: 9 },
              { name: '10 Colour Screenprint', variant_first: 'SP 10 CLR - First Print', variant_additional: 'SP 10 CLR - Additional Print', moq: 20, colours: 10 },
              { name: '11 Colour Screenprint', variant_first: 'SP 11 CLR - First Print', variant_additional: 'SP 11 CLR - Additional Print', moq: 20, colours: 11 },
              { name: '12 Colour Screenprint', variant_first: 'SP 12 CLR - First Print', variant_additional: 'SP 12 CLR - Additional Print', moq: 20, colours: 12 }
            ],
            printProducts: [],
            hasSize: config.hasSize || false,
            singleQuantity: 0,
            quantities: config.quantities || {},
            colourValues: config.colourValues || {},
            colourOverrides: config.colourOverrides || {},
            productHandle: config.productHandle || '',
            formId: config.formId || '',
            sectionId: config.sectionId || '',
            disableBuyBlank: config.disableBuyBlank || false,
            moneyFormat: config.moneyFormat || '${{amount}}',
            coloursWithValues: config.coloursWithValues || [],
          };
        },
        watch: {
          selectedColourName() {
            this.showColourOnPreview = true;
          }
        },
        methods: {
          selectHeroOption(heroOption) {
            this.buyType = 'custom';

            // Find the print type that matches the hero option variant
            let printType = this.availablePrintTypes.find(type => type.variant_first === heroOption.variant);

            // If not found in availablePrintTypes, check screenPrintOptions
            if (!printType) {
              printType = this.screenPrintOptions.find(opt => opt.variant_first === heroOption.variant);
            }

            if (printType) {
              // Special case for screen print: we want to show the color selection menu
              // rather than automatically picking the first option.
              if (heroOption.name === 'Screen Print') {
                this.screenPrintContextMenu = this.selectedPrints[0].selection;
              } else {
                this.selectedPrints[0].type = printType;
              }
            }

            // Smoothly scroll to the print method section
            this.$nextTick(() => {
              this.scrollToLocations();
            });
          },

          scrollToLocations() {
            const element = this.$refs.locationsSection;
            if (element) {
              const isMobile = window.innerWidth <= 768;
              const headerOffset = isMobile ? 124 : 100; // 106px mobile header + 18px padding, 82px desktop header + 18px padding
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          },

          async fetchPrintLocations() {
            this.printProducts = [];

            const fetchProductsPage = async (cursor = null) => {
              const queryString = `{
                collection(handle: "prints") {
                  products(first: 250${cursor ? `, after: "${cursor}"` : ''}) {
                    pageInfo {
                      hasNextPage
                      endCursor
                    }
                    edges {
                      node {
                        id
                        title
                        handle
                        featuredImage {
                          url(transform: {maxWidth: 100})
                        }
                        variants(first: 50) {
                          edges {
                            node {
                              id
                              title
                              availableForSale
                              quantityAvailable
                              price {
                                amount
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }`;

              try {
                const response = await fetch('/api/2024-01/graphql.json', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': config.storefrontToken || ''
                  },
                  body: JSON.stringify({ query: queryString })
                });

                const data = await response.json();

                if (data.data?.collection?.products?.edges) {
                  const newProducts = data.data.collection.products.edges.map(edge => {
                    const node = edge.node;
                    const price = {};
                    const validPrints = [
                      'First Print', 'Additional Print',
                      'SP 1 CLR - First Print', 'SP 1 CLR - Additional Print',
                      'SP 2 CLR - First Print', 'SP 2 CLR - Additional Print',
                      'SP 3 CLR - First Print', 'SP 3 CLR - Additional Print',
                      'SP 4 CLR - First Print', 'SP 4 CLR - Additional Print',
                      'SP 5 CLR - First Print', 'SP 5 CLR - Additional Print',
                      'SP 6 CLR - First Print', 'SP 6 CLR - Additional Print',
                      'SP 7 CLR - First Print', 'SP 7 CLR - Additional Print',
                      'SP 8 CLR - First Print', 'SP 8 CLR - Additional Print',
                      'SP 9 CLR - First Print', 'SP 9 CLR - Additional Print',
                      'SP 10 CLR - First Print', 'SP 10 CLR - Additional Print',
                      'SP 11 CLR - First Print', 'SP 11 CLR - Additional Print',
                      'SP 12 CLR - First Print', 'SP 12 CLR - Additional Print',
                      'First Embroidery', 'Additional Embroidery'
                    ];

                    if (node.variants?.edges) {
                      node.variants.edges.forEach(variantEdge => {
                        const variant = variantEdge.node;
                        const title = variant.title.includes(' / ')
                          ? variant.title.split(' / ').pop()
                          : variant.title;

                        for (const printType of validPrints) {
                          if (title === printType) {
                            price[printType] = {
                              price: parseFloat(variant.price.amount),
                              id: variant.id
                            };
                          }
                        }
                      });
                    }

                    return {
                      id: node.id,
                      title: node.title,
                      handle: node.handle,
                      image: node.featuredImage?.url || '',
                      price: price
                    };
                  });

                  this.printProducts = [...this.printProducts, ...newProducts];

                  const pageInfo = data.data.collection.products.pageInfo;
                  if (pageInfo.hasNextPage) {
                    await fetchProductsPage(pageInfo.endCursor);
                  }
                }
              } catch (error) {
                console.error('Error fetching print locations:', error);
              }
            };

            await fetchProductsPage();
          },

          async fetchAllVariants() {
            if (!config.productId || !config.storefrontToken) {
              console.warn('Missing productId or storefrontToken');
              return;
            }

            // Check if any additional products have been provided
            const additionalProductIds = config.additionalProductIds || [];
            const allProductIds = [config.productId, ...additionalProductIds.filter(id => id && id !== config.productId)];

            this.variants = [];

            for (const productId of allProductIds) {
              let hasNextPage = true;
              let cursor = null;

              while (hasNextPage) {
                const query = `
        query getVariants($id: ID!, $cursor: String) {
          product(id: $id) {
            variants(first: 250, after: $cursor) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  title
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                  }
                  image {
                    id
                  }
                }
              }
            }
          }
        }`;

                const variables = {
                  id: `gid://shopify/Product/${productId}`,
                  cursor: cursor
                };

                try {
                  const response = await fetch('/api/2026-01/graphql.json', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'X-Shopify-Storefront-Access-Token': config.storefrontToken
                    },
                    body: JSON.stringify({ query, variables })
                  });

                  const { data, errors } = await response.json();
                  if (errors) throw new Error(errors[0].message);
                  if (!data.product) {
                    console.warn(`Product not found: ${productId}`);
                    hasNextPage = false;
                    continue;
                  }

                  const variantEdges = data.product.variants.edges;
                  const pageInfo = data.product.variants.pageInfo;

                  const fetchedVariants = variantEdges.map(({ node }) => {
                    // Map array of options back to option1, option2, option3, matching to liquid variable
                    const optionsMap = {};
                    node.selectedOptions.forEach((opt, index) => {
                      optionsMap[`option${index + 1}`] = opt.value;
                    });

                    return {
                      id: node.id.split('/').pop(),
                      title: node.title,
                      ...optionsMap,
                      available: node.availableForSale,
                      price: parseFloat(node.price.amount) * 100, // Typically stored as cents in themes
                      featured_media: node.image ? {
                        id: node.image.id.split('/').pop(),
                      } : null
                    };
                  });

                  this.variants = [...this.variants, ...fetchedVariants];
                  hasNextPage = pageInfo.hasNextPage;
                  cursor = pageInfo.endCursor;

                } catch (error) {
                  console.error('GraphQL Fetch Error:', error);
                  hasNextPage = false;
                }
              }
            }

            // Set default selections based on the first variant found
            if (this.variants.length) {
              const first = this.variants[0];
              this.options = [first.option1 || null, first.option2 || null, first.option3 || null];
            }
          },

          scrollToSize() {
            const element = document.getElementById("sizeHeading");
            if (element) {
              const isMobile = window.innerWidth <= 768;
              const headerOffset = isMobile ? 124 : 100; // 106px mobile header + 18px padding, 82px desktop header + 18px padding
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          },

          scrollToServices() {
            const element = document.getElementById("servicesHeading");
            if (element) {
              const isMobile = window.innerWidth <= 768;
              const headerOffset = isMobile ? 124 : 100; // 106px mobile header + 18px padding, 82px desktop header + 18px padding
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          },

          syncHiddenVariantSelects(eventTarget) {
            // Find the hidden variant-selects element
            const variantSelects = document.querySelector('variant-selects[data-section="' + this.sectionId + '"]');
            if (!variantSelects) return;

            // Get the option index from the event target's data-index attribute
            const dataIndex = eventTarget.getAttribute('data-index');
            if (!dataIndex) return;

            // Extract the option number from "option1", "option2", etc.
            const optionIndex = parseInt(dataIndex.replace('option', '')) - 1;

            // Find the corresponding select element in the hidden variant-selects
            const hiddenSelect = variantSelects.querySelector(`select[data-product-builder-sync="${optionIndex}"]`);
            if (!hiddenSelect) return;

            // Update the selected option in the hidden select
            const selectedValue = eventTarget.value;
            Array.from(hiddenSelect.options).forEach(option => {
              if (option.value === selectedValue) {
                option.setAttribute('selected', 'selected');
                hiddenSelect.value = selectedValue;
              } else {
                option.removeAttribute('selected');
              }
            });

            // Update the variant data in the hidden script tag
            this.updateSelectedVariantData();
          },

          updateSelectedVariantData() {
            const variantSelects = document.querySelector('variant-selects[data-section="' + this.sectionId + '"]');
            if (!variantSelects) return;

            // Find the variant that matches the current option selections
            const selectedVariant = this.variants.find((variant) => {
              const opt1Match = this.sizeIndex !== 1 && variant.option1
                ? this.options[0] === variant.option1
                : true;
              const opt2Match = this.sizeIndex !== 2 && variant.option2
                ? this.options[1] === variant.option2
                : true;
              const opt3Match = this.sizeIndex !== 3 && variant.option3
                ? this.options[2] === variant.option3
                : true;

              return opt1Match && opt2Match && opt3Match;
            });

            // Update the data-selected-variant script content
            if (selectedVariant) {
              const scriptTag = variantSelects.querySelector('script[data-selected-variant]');
              if (scriptTag) {
                scriptTag.textContent = JSON.stringify(selectedVariant);
              }
            }
          },

          updateImage(event) {
            // Sync hidden variant-selects element
            this.syncHiddenVariantSelects(event.target);

            // Emit event to parent theme if FoxTheme pubsub is available
            if (window.FoxTheme && window.FoxTheme.pubsub) {
              const target = event.target;

              // Map our current options state to what the theme expects
              // Theme expects an array of option value IDs that are currently selected
              const selectedOptionValues = [];

              // Find the colour for the options
              this.options.forEach((val, index) => {
                if (val !== undefined && this.coloursWithValues[val]) {
                  selectedOptionValues.push(this.coloursWithValues[val]);
                }
              });

              // Add the default size
              if (config.defaultSizeId) {
                selectedOptionValues.push(config.defaultSizeId);
              }

              window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
                data: {
                  event,
                  target,
                  selectedOptionValues: selectedOptionValues,
                },
              });
            }

            const selectedVariants = this.variants.filter((row) => {
              const opt1Match = row.option1 && this.sizeIndex !== 1
                ? this.options[0] === row.option1
                : true;
              const opt2Match = row.option2 && this.sizeIndex !== 2
                ? this.options[1] === row.option2
                : true;
              const opt3Match = row.option3 && this.sizeIndex !== 3
                ? this.options[2] === row.option3
                : true;

              return opt1Match && opt2Match && opt3Match;
            });

            if (selectedVariants.length && selectedVariants[0]?.featured_media?.id) {
              const mediaItem = document.querySelector(
                `.product__media [data-media-id="${selectedVariants[0].featured_media.id}"]`
              );
              if (mediaItem) {
                mediaItem.click();
              }
            }
          },

          clearForm() {
            this.buyType = null;
            this.selectedPrints = [{
              selection: 0,
              type: null,
              id: null,
            }];
          },

          async submit() {
            let selectedVariants = this.variants.filter((row) => {
              const opt1Match = row.option1 && this.sizeIndex !== 1
                ? this.options[0] === row.option1
                : true;
              const opt2Match = row.option2 && this.sizeIndex !== 2
                ? this.options[1] === row.option2
                : true;
              const opt3Match = row.option3 && this.sizeIndex !== 3
                ? this.options[2] === row.option3
                : true;

              return opt1Match && opt2Match && opt3Match;
            });

            const stringLength = "gid://shopify/ProductVariant/".length;
            selectedVariants = selectedVariants.reverse();

            if (selectedVariants.length) {
              this.submitted = true;
              const randomKey = (Math.random() + 1).toString(36).substring(2);

              for (const selectedVariant of selectedVariants) {
                const optionKey = selectedVariant[`option${this.sizeIndex}`];
                const quantity = this.hasSize
                  ? Number(this.quantities[optionKey])
                  : Number(this.singleQuantity);

                if (quantity > 0) {
                  const products = [];
                  let initial = true;

                  products.push({
                    id: selectedVariant.id,
                    quantity: quantity,
                    properties: {
                      '_can_bulk_discount': 1
                    },
                  });

                  if (this.printBrief) {
                    products[0].properties.Brief = this.printBrief;
                  }

                  if (this.buyType === 'custom') {
                    products[0].properties._custom = `${selectedVariant.id}${randomKey}`;

                    this.selectedPrints.forEach((selectedPrint) => {
                      const optKey = (Math.random() + 1).toString(36).substring(2);
                      const print = this.availablePrints.find(({ id }) => id === selectedPrint.id);

                      if (print) {
                        const variantKey = initial
                          ? selectedPrint.type.variant_first
                          : selectedPrint.type.variant_additional;
                        const printId = print.price[variantKey]?.id?.substring(stringLength);

                        if (printId) {
                          const product = {
                            id: parseInt(printId),
                            quantity: quantity,
                            properties: {
                              _package: `${selectedVariant.id}${randomKey}`,
                              _print_key: optKey,
                            }
                          };
                          products.push(product);
                          initial = false;
                        }
                      }
                    });

                    // Add selected additional services
                    this.selectedServices.forEach((serviceId) => {
                      const service = this.additionalServices.find(s => s.id === serviceId);
                      if (service && service.variantId) {
                        // If the variantId is not a string, it doesn't need to be processed
                        const serviceVariantId = parseInt(typeof service.variantId === 'string' ? service.variantId.substring(stringLength) : service.variantId);
                        const serviceKey = (Math.random() + 1).toString(36).substring(2);

                        const serviceProduct = {
                          id: serviceVariantId,
                          quantity: quantity,
                          properties: {
                            _package: `${selectedVariant.id}${randomKey}`,
                            _service_key: serviceKey,
                          }
                        };
                        products.push(serviceProduct);
                      }
                    });
                  }

                  try {
                    await fetch(window.Shopify.routes.root + 'cart/add.js', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ items: products })
                    });
                  } catch (error) {
                    console.error('Error adding to cart:', error);
                  }
                }
              }

              this.submitted = false;
              this.success = true;

              // Trigger cart update event and auto-open drawer
              document.dispatchEvent(new CustomEvent('cart:refresh', {
                detail: { open: true }
              }));

              // Alternative: jQuery trigger for older themes
              if (window.jQuery) {
                window.jQuery('body').trigger('added.ajaxProduct');
              }
            }
          },

          addPrint() {
            if (this.availablePrints.length > this.selectedPrints.length) {
              this.selectedPrints.push({
                id: null,
                type: null,
                selection: this.selection++,
              });
            }
          },

          applyPrint(print, option) {
            if (!this.hasSelectedPrint(option)) {
              print.id = option.id;
            }
          },

          applyType(print, option) {
            if (option.isParent) {
              // Toggle context menu for screen print
              this.screenPrintContextMenu = this.screenPrintContextMenu === print.selection ? null : print.selection;
            } else {
              print.type = option;
              this.screenPrintContextMenu = null; // Close menu after selection
            }
          },

          selectScreenPrintOption(print, option) {
            print.type = option;
            this.screenPrintContextMenu = null; // Close menu after selection
          },

          closeScreenPrintMenu() {
            this.screenPrintContextMenu = null;
          },

          getAvailableScreenPrintOptions() {
            // Filter screen print options based on what's available in print products
            return this.screenPrintOptions.filter(option => {
              return this.availablePrints.some(({ price }) =>
                typeof price[option.variant_first] !== "undefined"
              );
            });
          },

          removePrint(print) {
            this.selectedPrints = this.selectedPrints.filter(row =>
              row.selection !== print.selection
            );
          },

          hasSelectedPrint(print) {
            return this.selectedPrints.some(row => row.id === print.id);
          },

          optionCanUsePrint(option, print, additional) {
            const variantKey = additional ? 'variant_additional' : 'variant_first';
            return typeof print.price[option[variantKey]] !== 'undefined';
          },

          canUseMethod(method) {
            if (typeof method === 'string') {
              return this.availablePrints.some(({ price }) =>
                typeof price[method] !== "undefined"
              );
            } else {
              // Handle parent Screen Print option - check if any child option is available
              if (method.isParent) {
                return this.screenPrintOptions.some(option => {
                  return this.availablePrints.some(({ price }) =>
                    typeof price[option.variant_first] !== "undefined"
                  );
                });
              }
              // Handle regular options
              return this.availablePrints.some(({ price }) =>
                typeof price[method['variant_first']] !== "undefined"
              );
            }
          },

          locationName(location) {
            return this.availablePrints.find(({ id }) => id === location)?.title || '';
          },

          locationImage(location) {
            return this.availablePrints.find(({ id }) => id === location)?.image || '';
          },

          formatMoney(cents) {
            // Convert cents to dollars
            const amount = (cents / 100).toFixed(2);

            // Parse the money format string
            // Common formats: ${{amount}}, {{amount}} USD, {{amount_with_comma_separator}}, etc.
            let formatted = this.moneyFormat;

            // Handle different amount placeholders
            if (formatted.includes('{{amount_with_comma_separator}}')) {
              // Format with comma as decimal separator (e.g., European format)
              const amountWithComma = amount.replace('.', ',');
              formatted = formatted.replace('{{amount_with_comma_separator}}', amountWithComma);
            } else if (formatted.includes('{{amount_no_decimals}}')) {
              // Format without decimals
              const amountNoDecimals = Math.round(cents / 100);
              formatted = formatted.replace('{{amount_no_decimals}}', amountNoDecimals);
            } else if (formatted.includes('{{amount_with_space_separator}}')) {
              // Format with space as thousand separator
              const parts = amount.split('.');
              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
              formatted = formatted.replace('{{amount_with_space_separator}}', parts.join('.'));
            } else if (formatted.includes('{{amount}}')) {
              // Standard format with period decimal
              formatted = formatted.replace('{{amount}}', amount);
            } else {
              // Fallback - just return with dollar sign
              formatted = `$${amount}`;
            }

            return formatted;
          },
        },

        computed: {
          finalMoq() {
            let moq = 0;
            const productMoq = Number(this.cartLimit?.min || 0);

            this.selectedPrints.forEach(({ type }) => {
              if (type && type.moq > moq) {
                moq = type.moq;
              }
            });

            return productMoq > moq ? productMoq : moq;
          },

          availablePrints() {
            return this.printProducts
              .filter(row => this.positions.includes(row.handle))
              .sort((a, b) => this.positions.indexOf(a.handle) - this.positions.indexOf(b.handle));
          },

          availableAdditionalServices() {
            return this.additionalServices.filter(service => service.id && service.title);
          },

          step() {
            if (this.buyType === 'blank') return 3;
            if (!this.selectedPrints) return 1;
            if (this.selectedPrints.some(({ id }) => !id)) return 2;
            return 3;
          },

          noSizeSelections() {
            return !Object.values(this.quantities).some(qty => qty > 0)
              && Number(this.singleQuantity) === 0;
          },

          quantityLimitUnder() {
            if (this.finalMoq) {
              const total = Object.values(this.quantities)
                .reduce((b, a) => Number(b) + Number(a), 0);
              return total < this.finalMoq;
            }
            return false;
          },

          quantityLimitOver() {
            if (this.cartLimit === null) return false;
            if (this.cartLimit.max) {
              const total = Object.values(this.quantities)
                .reduce((b, a) => Number(b) + Number(a), 0);
              return total > this.cartLimit.max;
            }
            return false;
          }
        },

        beforeMount() {
          if (Object.values(this.colourOverrides).length) {
            Object.assign(this.colourValues, this.colourOverrides);
          }
        },

        async mounted() {
          await Promise.all([
            this.fetchPrintLocations(),
            this.fetchAllVariants()
          ]);

          // Check for cart limits
          const checkLimits = setInterval(() => {
            if (window.minMaxify) {
              this.cartLimit = { ...window.minMaxify.getLimitsFor({ handle: this.productHandle }) };
              clearInterval(checkLimits);
            }
          }, 1000);
        },
      });

      this.app.mount(this.querySelector('[data-builder-app]'));
    }

    disconnectedCallback() {
      if (this.app) {
        this.app.unmount();
      }
    }
  }

  customElements.define('product-builder-form', ProductBuilderForm);
}
